/**
 * TripWizardSheet — web port. Four-step wizard for kicking off an AI trip
 * generation: Destination → Dates → Travelers → Style. After submit, the
 * wizard closes immediately and TripGenerationContext handles polling +
 * the completion toast.
 *
 * Streamlined from the RN component (reanimated entry/exit animations
 * dropped in favour of CSS; chips simplified). Same options/validation,
 * same submission flow.
 */

import { useCallback, useEffect, useState } from 'react';
import Modal from '../../common/Modal';
import Icon from '../../common/Icon';
import { tripPlanningApi } from '../../api/tripPlanningApi';
import { useTripGeneration } from '../../context/TripGenerationContext';
import { useToast } from '../../context/ToastContext';
import type {
  TripGenerationInput,
  TripPurpose,
  BudgetTier,
  PacePreference,
  CoTraveler,
} from '@wg/types';
import './TripWizardSheet.css';

type WizardStep = 'destination' | 'dates' | 'travelers' | 'style';

interface Props {
  visible: boolean;
  onClose: () => void;
  initialDestination?: string;
}

interface ChipOption<T> {
  value: T;
  label: string;
  icon?: string;
  description?: string;
}

const PURPOSE_OPTIONS: ChipOption<TripPurpose>[] = [
  { value: 'vacation', label: 'Vacation', icon: 'sunny-outline' },
  { value: 'adventure', label: 'Adventure', icon: 'compass-outline' },
  { value: 'relaxation', label: 'Relaxation', icon: 'leaf-outline' },
  { value: 'cultural', label: 'Cultural', icon: 'business-outline' },
  { value: 'foodie', label: 'Foodie', icon: 'restaurant-outline' },
  { value: 'family', label: 'Family', icon: 'people-outline' },
  { value: 'honeymoon', label: 'Romance', icon: 'heart-outline' },
  { value: 'business', label: 'Business', icon: 'briefcase-outline' },
];

const BUDGET_OPTIONS: ChipOption<BudgetTier>[] = [
  { value: 'budget', label: 'Budget', icon: 'wallet-outline' },
  { value: 'moderate', label: 'Moderate', icon: 'cash-outline' },
  { value: 'premium', label: 'Premium', icon: 'diamond-outline' },
  { value: 'luxury', label: 'Luxury', icon: 'sparkles-outline' },
];

const PACE_OPTIONS: ChipOption<PacePreference>[] = [
  {
    value: 'relaxed',
    label: 'Relaxed',
    icon: 'bed-outline',
    description: '2-3/day',
  },
  {
    value: 'moderate',
    label: 'Balanced',
    icon: 'walk-outline',
    description: '4-5/day',
  },
  {
    value: 'active',
    label: 'Active',
    icon: 'bicycle-outline',
    description: '6-7/day',
  },
  {
    value: 'intensive',
    label: 'Packed',
    icon: 'flash-outline',
    description: 'Max!',
  },
];

const TRAVELER_PRESETS: ChipOption<string>[] = [
  { value: 'solo', label: 'Solo', icon: 'person-outline' },
  { value: 'couple', label: 'Couple', icon: 'heart-outline' },
  { value: 'family', label: 'Family', icon: 'people-outline' },
  { value: 'group', label: 'Group', icon: 'people-circle-outline' },
];

const STEPS: WizardStep[] = ['destination', 'dates', 'travelers', 'style'];

function validateDateString(s: string | undefined): string | null {
  if (!s) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'Use format YYYY-MM-DD';
  const [y, m, d] = s.split('-').map(Number);
  const date = new Date(s + 'T00:00:00');
  if (isNaN(date.getTime())) return 'Invalid date';
  if (
    date.getFullYear() !== y ||
    date.getMonth() + 1 !== m ||
    date.getDate() !== d
  ) {
    return 'Invalid date (check month/day)';
  }
  return null;
}

export default function TripWizardSheet({
  visible,
  onClose,
  initialDestination,
}: Props) {
  const { trackGeneration } = useTripGeneration();
  const { showToast } = useToast();

  const [step, setStep] = useState<WizardStep>('destination');
  const [input, setInput] = useState<TripGenerationInput>({
    destination: initialDestination || '',
    duration_days: 3,
    trip_purpose: 'vacation',
    budget_tier: 'moderate',
    pace_preference: 'moderate',
  });
  const [travelerPreset, setTravelerPreset] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      const timer = setTimeout(() => {
        setStep('destination');
        setTravelerPreset(null);
        setSubmitting(false);
        setError(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  const update = <K extends keyof TripGenerationInput>(
    k: K,
    v: TripGenerationInput[K],
  ) => setInput(prev => ({ ...prev, [k]: v }));

  const validateDates = useCallback((): string | null => {
    const startErr = validateDateString(input.start_date);
    if (startErr) return `Start date: ${startErr}`;
    const endErr = validateDateString(input.end_date);
    if (endErr) return `End date: ${endErr}`;
    if (input.start_date && input.end_date) {
      if (new Date(input.start_date) > new Date(input.end_date)) {
        return 'Start date must be before end date';
      }
    }
    return null;
  }, [input.start_date, input.end_date]);

  const goNext = () => {
    const idx = STEPS.indexOf(step);
    if (step === 'dates') {
      const err = validateDates();
      if (err) {
        setError(err);
        return;
      }
    }
    if (step === 'destination' && !input.destination.trim()) {
      setError('Please enter a destination');
      return;
    }
    setError(null);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1]);
  };

  const goBack = () => {
    const idx = STEPS.indexOf(step);
    setError(null);
    if (idx > 0) setStep(STEPS[idx - 1]);
  };

  const applyTravelerPreset = (preset: string) => {
    setTravelerPreset(preset);
    let travelers: CoTraveler[] = [];
    switch (preset) {
      case 'solo':
        travelers = [{ type: 'adult', count: 1 }];
        break;
      case 'couple':
        travelers = [{ type: 'adult', count: 2 }];
        break;
      case 'family':
        travelers = [
          { type: 'adult', count: 2 },
          { type: 'child', count: 2 },
        ];
        break;
      case 'group':
        travelers = [{ type: 'adult', count: 4 }];
        break;
    }
    update('co_travelers', travelers);
  };

  const handleSubmit = async () => {
    const dateErr = validateDates();
    if (dateErr) {
      setError(dateErr);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await tripPlanningApi.generateTrip(input);
      trackGeneration(result.request_id, input.destination);
      showToast(`Creating your ${input.destination} trip…`, {
        variant: 'info',
        duration: 3000,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to start trip generation');
      setSubmitting(false);
    }
  };

  const isLast = step === 'style';
  const stepTitle: Record<WizardStep, string> = {
    destination: 'Where to?',
    dates: 'When?',
    travelers: 'Who is traveling?',
    style: 'How do you like to travel?',
  };

  return (
    <Modal visible={visible} onClose={onClose} title={stepTitle[step]}>
      <div className="wg-wizard">
        <div className="wg-wizard-stepper">
          {STEPS.map(s => (
            <div
              key={s}
              className={`wg-wizard-step-dot${s === step ? ' active' : ''}`}
            />
          ))}
        </div>

        {step === 'destination' && (
          <div className="wg-wizard-section">
            <label className="wg-wizard-label">Destination</label>
            <input
              className="wg-wizard-input"
              type="text"
              placeholder="e.g., Oahu, Hawaii"
              value={input.destination}
              onChange={e => update('destination', e.target.value)}
              autoFocus
            />
          </div>
        )}

        {step === 'dates' && (
          <>
            <div className="wg-wizard-section">
              <label className="wg-wizard-label">Duration (days)</label>
              <input
                className="wg-wizard-input"
                type="number"
                min={1}
                max={30}
                value={input.duration_days || ''}
                onChange={e =>
                  update('duration_days', Number(e.target.value) || undefined)
                }
              />
            </div>
            <div className="wg-wizard-row">
              <div className="wg-wizard-section">
                <label className="wg-wizard-label">Start date (optional)</label>
                <input
                  className="wg-wizard-input"
                  type="date"
                  value={input.start_date || ''}
                  onChange={e =>
                    update('start_date', e.target.value || undefined)
                  }
                />
              </div>
              <div className="wg-wizard-section">
                <label className="wg-wizard-label">End date (optional)</label>
                <input
                  className="wg-wizard-input"
                  type="date"
                  value={input.end_date || ''}
                  onChange={e =>
                    update('end_date', e.target.value || undefined)
                  }
                />
              </div>
            </div>
          </>
        )}

        {step === 'travelers' && (
          <div className="wg-wizard-section">
            <label className="wg-wizard-label">Group</label>
            <div className="wg-wizard-chips">
              {TRAVELER_PRESETS.map(p => (
                <button
                  key={p.value}
                  type="button"
                  className={`wg-wizard-chip${
                    travelerPreset === p.value ? ' selected' : ''
                  }`}
                  onClick={() => applyTravelerPreset(p.value)}
                >
                  {p.icon && <Icon name={p.icon} size={18} color="#fff" />}
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'style' && (
          <>
            <div className="wg-wizard-section">
              <label className="wg-wizard-label">Trip purpose</label>
              <div className="wg-wizard-chips">
                {PURPOSE_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    className={`wg-wizard-chip${
                      input.trip_purpose === o.value ? ' selected' : ''
                    }`}
                    onClick={() => update('trip_purpose', o.value)}
                  >
                    {o.icon && <Icon name={o.icon} size={18} color="#fff" />}
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="wg-wizard-section">
              <label className="wg-wizard-label">Budget</label>
              <div className="wg-wizard-chips">
                {BUDGET_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    className={`wg-wizard-chip${
                      input.budget_tier === o.value ? ' selected' : ''
                    }`}
                    onClick={() => update('budget_tier', o.value)}
                  >
                    {o.icon && <Icon name={o.icon} size={18} color="#fff" />}
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="wg-wizard-section">
              <label className="wg-wizard-label">Pace</label>
              <div className="wg-wizard-chips">
                {PACE_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    className={`wg-wizard-chip${
                      input.pace_preference === o.value ? ' selected' : ''
                    }`}
                    onClick={() => update('pace_preference', o.value)}
                  >
                    {o.icon && <Icon name={o.icon} size={18} color="#fff" />}
                    {o.label}
                    {o.description && (
                      <span className="wg-wizard-chip-desc">
                        {o.description}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {error && <p className="wg-wizard-error">{error}</p>}

        <div className="wg-wizard-actions">
          <button
            type="button"
            onClick={step === 'destination' ? onClose : goBack}
            disabled={submitting}
          >
            {step === 'destination' ? 'Cancel' : 'Back'}
          </button>
          <button
            type="button"
            className="primary"
            onClick={isLast ? handleSubmit : goNext}
            disabled={
              submitting ||
              (step === 'destination' && !input.destination.trim())
            }
          >
            {isLast ? (submitting ? 'Creating…' : 'Create trip') : 'Next'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
